import {Component, computed, inject, input, OnInit, output, signal} from '@angular/core';
import {PreferredTimeOff} from '../../../classes/models/employee/PreferredTimeOff';
import {asIsoWeekday, DayOfWeek, dayOfWeekValues} from '../../../classes/enums/DayOfWeek';
import moment from 'moment';
import {DataService} from '../../../services/store/data.service';
import {WeekTimeSlotsService} from '../../../services/week-time-slot.service';
import {TableHeader} from '../../../shared/classes/TableHeader';
import {TableSort} from '../../../shared/classes/TableSort';
import {TemporaryPreferredTimeOff} from '../../../classes/models/employee/TemporaryPreferredTimeOff';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {WorkingHoursService} from '../../../services/store/working-hours.service';
import {StoreDetails} from '../../../classes/models/store/StoreDetails';
import {EditType} from '../../../classes/enums/EditType';
import {StoreWorkingTime} from '../../../classes/models/store/StoreWorkingTime';
import {TimeOffModalComponent} from './time-off-modal/time-off-modal.component';
import {EmployeePositionId} from '../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from "../../../shared/standalone-imports";
import {WeekSelectComponent} from "../../../shared/components/week-select/week-select.component";

/**
 * Preferred time off item to be displayed in the list. Most field names are used for sorting the table - it is defined in
 * {@link PreferredTimeOffComponent.headers}.
 */
class PreferredTimeOffListItem {
  /** A date of a preferred time off. */
  date: string;
  /** The number of the day of week, when an employee prefers to have a day off. */
  isoDayOfWeek: number;
  /** A time of a preferred time off. */
  time: string;
  /** A reason of a preferred time off. */
  reason: string;
  /** An original object of an ongoing or temporary preferred time off. */
  originalObject: PreferredTimeOff | TemporaryPreferredTimeOff;

  constructor(preferredTimeOff: PreferredTimeOff | TemporaryPreferredTimeOff) {
    this.date = preferredTimeOff.getFormattedDate();
    this.isoDayOfWeek = moment(preferredTimeOff.dayOfWeek, 'dddd').isoWeekday();
    this.time = preferredTimeOff.getFormattedTime();
    this.reason = preferredTimeOff.reason;
    this.originalObject = preferredTimeOff;
  }
}

/**
 * This component handles displaying and editing preferred times off.
 */
@Component({
  selector: 'app-preferred-time-off',
  templateUrl: './preferred-time-off.component.html',
  styleUrls: ['./preferred-time-off.component.less'],
  imports: [SHARED_IMPORTS, WeekSelectComponent]
})
export class PreferredTimeOffComponent implements OnInit {

  /** Identifiers of an employee and position. */
  readonly employeePositionId = input.required<EmployeePositionId>();
  /** Detailed information about a store, assigned to an employee. */
  readonly storeDetails = input.required<StoreDetails>();
  /** A map of date to ongoing preferred times off list. */
  readonly preferredTimesOff = input.required<Map<string, PreferredTimeOff[]>>();
  /** A map of date to temporary preferred times off list. */
  readonly temporaryPreferredTimesOff = input.required<Map<string, TemporaryPreferredTimeOff>>();
  /** An event to refresh employee page data. */
  readonly getEmployeePageDataEvent = output();

  /** `True`, if a list is displayed. `False`, if a calendar is displayed. */
  readonly isListActive = signal(true);
  /** A column and direction for table to be sorted. */
  readonly tableSortEvent = signal<TableSort | undefined>(undefined);
  /** Sortable headers of preferred times off table. */
  readonly headers: TableHeader[] = [
    new TableHeader('Date', 'isoDayOfWeek', true),
    new TableHeader('Time', 'time', true),
    new TableHeader('Reason', 'reason', true),
    new TableHeader('', '', false)
  ];

  /** All values of {@link DayOfWeek}. */
  readonly weekdays: DayOfWeek[] = dayOfWeekValues();
  /** A cell height of preferred time off grid. */
  readonly cellHeight = 40;
  /** The selected Monday, from which starts preferred times off, who are showed in the grid. */
  readonly selectedMonday = signal<string>('');
  /** The first date, from which preferred times off can be edited. */
  readonly editDate = signal<string>('');
  /** Assigns user edit action type, so it can be used in HTML. */
  readonly EditType = EditType;

  /** Preferred times off for the selected week, derived from selectedMonday and preferredTimesOff input. */
  readonly weekPreferredTimesOff = computed(() => {
    return this.weekTimeSlotsService.getWeekData(this.selectedMonday(), this.preferredTimesOff());
  });

  /** Each hour from store opening until store closing, derived from selectedMonday and storeDetails input. */
  readonly hours = computed(() => {
    const storeDetails = this.storeDetails();
    const monday = this.selectedMonday();
    if (!storeDetails || !monday) {
      return [];
    }
    return this.getStoreHours(monday, storeDetails);
  });

  /** Preferred times off items to be displayed in the list, derived from week data and temporary times off. */
  readonly weekPreferredTimesOffList = computed(() => {
    const weekData = this.weekPreferredTimesOff();
    const temporaryData = this.temporaryPreferredTimesOff();
    const monday = this.selectedMonday();
    const list: PreferredTimeOffListItem[] = [];
    dayOfWeekValues().forEach(weekday => {
      const date = moment(monday).isoWeekday(weekday).format('YYYY-MM-DD');
      const temporaryPreferredTimeOff = temporaryData.get(date);
      const preferredTimeOff = temporaryPreferredTimeOff ? temporaryPreferredTimeOff : weekData.get(weekday);
      if (preferredTimeOff && preferredTimeOff.startTime) {
        list.push(new PreferredTimeOffListItem(preferredTimeOff));
      }
    });
    return list;
  });

  /** Gets the latest temporary preferred time off Monday in order to find out how many weeks of preferred times off should be showed. */
  readonly latestMonday = computed(() => {
    const nextWeekMonday = this.editDate();
    return Array.from(this.temporaryPreferredTimesOff().values())
            .map(preferredTimeOff => moment(preferredTimeOff.date).isoWeekday(1).format('YYYY-MM-DD'))
            .reduce((previous, current) => current > previous ? current : previous, nextWeekMonday);
  });

  private readonly weekTimeSlotsService = inject(WeekTimeSlotsService);
  private readonly modalService = inject(NgbModal);
  private readonly workingHoursService = inject(WorkingHoursService);
  readonly dataService = inject(DataService);

  /**
   * Sets up data, needed on page initialization.
   */
  ngOnInit(): void {
    if (this.dataService.selectedStore) {
      const currentWeekMonday = moment().startOf('week').isoWeekday(1).format('YYYY-MM-DD');
      this.selectedMonday.set(currentWeekMonday);
      this.editDate.set(this.dataService.editDate);
    }
  }

  /**
   * Updates the selected Monday, triggering recomputation of all derived data.
   * @param weekStart the Monday, from which the selected week starts.
   */
  selectWeek(weekStart: string): void {
    this.selectedMonday.set(weekStart);
  }

  private getStoreHours(monday: string, storeDetails: StoreDetails): string[] {
    const storeHoursFrom = this.workingHoursService.getStoreOpeningTimesStartingAt(monday, storeDetails);
    const fromHour = storeHoursFrom.reduce(((previous, current) =>
            previous.isBefore(current) ? previous : current), moment('23', 'HH'));

    const storeHoursTo = this.workingHoursService.getStoreClosingTimesStartingAt(monday, storeDetails);
    const toHour = storeHoursTo.reduce(((previous, current) =>
            previous.isAfter(current) ? previous : current), moment('01', 'HH'));

    return this.workingHoursService.calculateHoursRange(fromHour, toHour);
  }

  /**
   * Sorts the table by the given column in the given direction.
   * @param tableSortEvent a column and direction, in which table should be sorted.
   */
  onSort(tableSortEvent: TableSort): void {
    this.tableSortEvent.set(tableSortEvent);
  }

  /**
   * Checks if a preferred time off can be edited on the selected week.
   */
  canEdit(): boolean {
    return this.dataService.canEditAtDate(this.selectedMonday());
  }

  /**
   * Gets a temporary preferred time off, if it exists. Otherwise, gets an ongoing preferred time off.
   * @param dayOfWeek day of week, at which a preferred time off might exist.
   */
  getPreferredTimeOff(dayOfWeek: DayOfWeek): PreferredTimeOff | TemporaryPreferredTimeOff | undefined {
    const date = moment(this.selectedMonday()).isoWeekday(dayOfWeek).format('YYYY-MM-DD');
    const temporaryPreferredTimeOff = this.temporaryPreferredTimesOff().get(date);
    return temporaryPreferredTimeOff ? temporaryPreferredTimeOff : this.weekPreferredTimesOff().get(dayOfWeek);
  }

  /**
   * Gets start time from which preferred time's off HTML <div> element will be displayed. This method ensures that start time will not
   * be displayed earlier than store opening time because the grid starts from store opening time.
   * @param preferredTimeOff a preferred time off to be displayed.
   */
  getStartTimeForCalendar(preferredTimeOff: PreferredTimeOff | TemporaryPreferredTimeOff): string {
    return preferredTimeOff.startTime < this.hours()[0] ? this.hours()[0] : preferredTimeOff.startTime;
  }

  /**
   * Gets end time until which preferred time's off HTML <div> element will be displayed. This method ensures that end time will not
   * be displayed later than store closing time because the grid end at store closing time.
   * @param preferredTimeOff a preferred time off to be displayed.
   */
  getEndTimeForCalendar(preferredTimeOff: PreferredTimeOff | TemporaryPreferredTimeOff): string {
    const gridEnd = this.hours()[this.hours().length - 1];
    return preferredTimeOff.endTime > gridEnd ? gridEnd : preferredTimeOff.endTime;
  }

  /**
   * Opens a modal window to add a new preferred time off to the calendar.
   */
  addNewDayToACalendar(): void {
    const modalRef = TimeOffModalComponent.open(this.modalService, {
      editType: EditType.CREATE,
      employeePositionId: this.employeePositionId(),
      startDate: this.editDate(),
      weeklyPreferredTimesOff: this.weekPreferredTimesOff(),
      storeWorkingTime: new StoreWorkingTime(this.hours()[0], this.hours()[this.hours().length - 1]),
      weekExists: !!this.preferredTimesOff().get(this.editDate()),
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to update a preferred time off in a week.
   * @param preferredTimeOff a preferred time off, a user currently editing.
   * @param editType an edit action type, which is performed by a user.
   */
  editCalendarDay(preferredTimeOff: PreferredTimeOff | TemporaryPreferredTimeOff, editType: EditType): void {
    const isoWeekday = asIsoWeekday(preferredTimeOff.dayOfWeek);
    const date = moment(this.selectedMonday()).isoWeekday(isoWeekday).format('YYYY-MM-DD');
    const modalRef = TimeOffModalComponent.open(this.modalService, {
      editType,
      employeePositionId: this.employeePositionId(),
      startDate: this.editDate(),
      dayOfWeek: preferredTimeOff.dayOfWeek,
      weeklyPreferredTimesOff: this.weekPreferredTimesOff(),
      temporaryPreferredTimeOff: this.temporaryPreferredTimesOff().get(date),
      storeWorkingTime: new StoreWorkingTime(this.hours()[0], this.hours()[this.hours().length - 1]),
      weekExists: !!this.preferredTimesOff().get(this.editDate()),
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }
}